import React from 'react';
import { motion } from 'framer-motion';
import { HiDocumentText, HiUser, HiVideoCamera, HiPhotograph, HiMicrophone, HiFire } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { HiUserCircle } from 'react-icons/hi2';
import { BsStars } from 'react-icons/bs';

const creationOptions = [
    {
        id: 'post',
        title: 'Create Post',
        description: 'Craft engaging social media posts',
        icon: HiDocumentText,
        gradient: 'from-blue-500 via-cyan-500 to-teal-500',
        action: 'select-post-type',
    },
    {
        id: 'ai-creative-studio',
        title: 'AI Creative Studio',
        description: 'Generate stunning marketing creatives with AI',
        icon: BsStars,
        gradient: 'from-indigo-500 via-purple-500 to-pink-500',
        action: 'select-ai-creative-studio',
    },
    {
        id: 'avatar',
        title: 'Create Avatar',
        description: 'Generate AI avatars for videos',
        icon: HiUser,
        gradient: 'from-purple-500 via-pink-500 to-rose-500',
        action: '/ai-avatars?createModal=true',
    },
    {
        id: 'avatar-video',
        title: 'Create Avatar Video',
        description: 'Produce AI-powered avatar videos',
        icon: HiVideoCamera,
        gradient: 'from-pink-500 via-red-500 to-orange-500',
        action: '/ai-avatars-create',
    },
    {
        id: 'avatar-image',
        title: 'Create Avatar Image',
        description: 'Create stunning AI-generated avatar images',
        icon: HiUserCircle,
        gradient: 'from-lime-500 via-green-400 to-emerald-500',
        action: '/ai-avatar-images',
    },
    {
        id: 'viral-short-creator',
        title: 'Viral Short Creator',
        description: 'Create faceless viral shorts for TikTok, Reels & YouTube Shorts',
        icon: HiFire,
        gradient: 'from-green-500 via-emerald-500 to-teal-500',
        action: '/viral-short-creator',
    },
    {
        id: 'ai-image',
        title: 'Generate AI Image',
        description: 'Create stunning AI-generated images',
        icon: HiPhotograph,
        gradient: 'from-orange-500 via-amber-500 to-yellow-500',
        action: '/ai-image',
    },
    {
        id: 'ai-video',
        title: 'Generate AI Video',
        description: 'Create stunning AI-generated videos',
        icon: HiVideoCamera,
        gradient: 'from-fuchsia-500 via-violet-500 to-indigo-500',
        action: '/ai-videos',
    },
];

const CreateOptionsView = ({ onSelectPostType, onSelectAICreativeStudio, onClose, isDarkMode }) => {

    const navigate = useNavigate();

    const handleCardClick = (option) => {
        if (option.action === 'select-post-type') {
            onSelectPostType();
        } else if (option.action === 'select-ai-creative-studio') {
            onSelectAICreativeStudio();
        } else {
            // Navigate to the specified route and close modal
            navigate(option.action);
            onClose();
        }
    };

    return (
        <div className="p-8 md:p-12">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
            >
                <h1 className="text-4xl font-bold mb-3 text-white">
                    What would you like to create?
                </h1>
                <p className="text-lg text-gray-300">
                    Choose from our powerful AI-driven creation tools
                </p>
            </motion.div>

            {/* Creation Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
                {creationOptions.map((option, index) => {
                    const Icon = option.icon;
                    
                    return (
                        <motion.div
                            key={option.id}
                            initial={{ opacity: 0, y: 20, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{
                                duration: 0.4,
                                delay: index * 0.1,
                                type: 'spring',
                                stiffness: 300,
                                damping: 24,
                            }}
                            whileHover={{
                                scale: 1.05,
                                transition: { duration: 0.2 },
                            }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleCardClick(option)}
                            className="relative cursor-pointer group"
                        >
                            {/* Card Container */}
                            <div className="relative overflow-hidden rounded-2xl p-4 lg:p-6 h-full transition-all duration-300 bg-white/5 backdrop-blur-xl hover:bg-white/10 shadow-2xl hover:shadow-[0_0_40px_rgba(255,255,255,0.1)] border border-white/10">
                                {/* Gradient Overlay on Hover */}
                                <div className={`
                                    absolute inset-0 opacity-0 group-hover:opacity-20 
                                    transition-opacity duration-300
                                    bg-gradient-to-br ${option.gradient}
                                `} />

                                {/* Floating Decorative Shapes */}
                                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full transform translate-x-8 -translate-y-8 blur-2xl" />
                                <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full transform -translate-x-6 translate-y-6 blur-xl" />

                                {/* Hover "Get Started" Badge - Top Right */}
                                <div className={`
                                    absolute top-4 right-4 z-20
                                    opacity-0 group-hover:opacity-100
                                    transform scale-90 group-hover:scale-100
                                    transition-all duration-300
                                `}>
                                    <div className={`
                                        flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                        bg-gradient-to-r ${option.gradient}
                                        shadow-lg
                                    `}>
                                        <span className="text-xs font-semibold text-white whitespace-nowrap">
                                            Get Started
                                        </span>
                                        <svg 
                                            className="w-3 h-3 text-white"
                                            fill="currentColor" 
                                            viewBox="0 0 20 20"
                                        >
                                            <path 
                                                fillRule="evenodd" 
                                                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" 
                                                clipRule="evenodd" 
                                            />
                                        </svg>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="relative z-10 hidden lg:block">
                                    {/* Icon with Gradient Background */}
                                    <div className={`
                                        inline-flex p-3 rounded-xl mb-4
                                        bg-gradient-to-br ${option.gradient}
                                        shadow-lg
                                        transform group-hover:scale-110 group-hover:rotate-3
                                        transition-all duration-300
                                    `}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>

                                    {/* Title */}
                                    <h3 className="!text-lg lg:!text-xl font-bold mb-2 text-white">
                                        {option.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-sm text-gray-300 leading-relaxed">
                                        {option.description}
                                    </p>
                                </div>

                                <div className="relative z-10 flex items-center gap-4 lg:hidden">
                                    {/* Icon with Gradient Background */}
                                    <div className={`
                                        inline-flex p-3 rounded-xl
                                        bg-gradient-to-br ${option.gradient}
                                        shadow-lg
                                        transform group-hover:scale-110 group-hover:rotate-3
                                        transition-all duration-300
                                    `}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>

                                    <div className="flex flex-col">
                                        {/* Title */}
                                        <h3 className="!text-lg lg:!text-xl font-bold mb-0.5 text-white">
                                            {option.title}
                                        </h3>

                                        {/* Description */}
                                        <p className="text-sm text-gray-300 leading-relaxed">
                                            {option.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};

export default CreateOptionsView;

